/**
 * Admin portal gate — hides /admin/login and /super-admin/login behind a
 * rotating slug. The slug is NEVER baked into the frontend bundle — it is
 * validated server-side via POST /api/v1/auth/admin-portal/probe.
 *
 * Flow:
 *   1. Admin visits /admin/login?portal=<slug> (shared out-of-band).
 *   2. PortalGate POSTs the slug to the backend probe endpoint.
 *   3. If backend returns {ok: true}, slug is stashed in sessionStorage and
 *      the admin login form renders. Otherwise a plain 404 renders so
 *      scanners can't fingerprint the admin surface.
 *   4. Subsequent navigation within the admin UI reuses the stashed slug;
 *      the slug is dropped from the URL via replaceState after validation.
 *
 * This is defense-in-depth — real security comes from the server-side
 * require_roles("admin"|"super_admin") on every admin API endpoint.
 */
import { useEffect, useState } from "react";

const SLUG_STORAGE_KEY = "ddip_admin_portal_slug";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export function getStoredSlug() {
    try {
        return sessionStorage.getItem(SLUG_STORAGE_KEY) || "";
    } catch { return ""; }
}

function storeSlug(v) {
    try { sessionStorage.setItem(SLUG_STORAGE_KEY, v); } catch { /* ignore */ }
}

async function probeSlug(slug) {
    try {
        const r = await fetch(`${BACKEND}/api/v1/auth/admin-portal/probe`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ slug }),
        });
        if (!r.ok) return false;
        const data = await r.json();
        return !!data.ok;
    } catch { return false; }
}

export default function PortalGate({ children }) {
    // Default-deny: assume the plausible-404 state on first paint. The
    // effect flips to `true` ONLY after a successful server-side probe.
    // Any gap (no param, no stored slug, stale slug, network error during
    // probe) keeps the component in the 404 state, so an attacker never
    // sees admin login UI even for a frame.
    const [allowed, setAllowed] = useState(false);
    const [probing, setProbing] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams(window.location.search);
        const provided = params.get("portal") || getStoredSlug();
        if (!provided) {
            // No slug at all — keep allowed=false, stop probing immediately.
            setProbing(false);
            return () => { cancelled = true; };
        }
        probeSlug(provided).then((ok) => {
            if (cancelled) return;
            if (ok) {
                storeSlug(provided);
                if (params.has("portal")) {
                    params.delete("portal");
                    const clean = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
                    window.history.replaceState({}, "", clean);
                }
                setAllowed(true);
            }
            setProbing(false);
        });
        return () => { cancelled = true; };
    }, []);

    if (allowed) return children;
    // Not allowed — render the plausible 404 on first paint AND throughout
    // the probe. We briefly flash a very faint "..." if we're probing a
    // stored slug (so an admin returning via sessionStorage doesn't see
    // the 404 flash), but if `probing` is true because there's a stored
    // slug an attacker shouldn't see admin login UI either way.
    if (probing) {
        return (
            <div style={{
                minHeight: "70vh", display: "flex", alignItems: "center",
                justifyContent: "center",
                fontFamily: "system-ui, -apple-system, sans-serif", color: "#94a3b8",
            }} data-testid="portal-probing" aria-hidden="true" />
        );
    }
    // Render a plausible SPA 404 so scanners can't fingerprint us.
    return (
        <div style={{
            minHeight: "70vh", display: "flex", alignItems: "center",
            justifyContent: "center", flexDirection: "column",
            fontFamily: "system-ui, -apple-system, sans-serif", color: "#334155",
        }} data-testid="portal-404">
            <h1 style={{ fontSize: 72, margin: 0, color: "#0f172a" }}>404</h1>
            <p style={{ marginTop: 8 }}>This page could not be found.</p>
        </div>
    );
}
